import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import _ from 'lodash';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Toaster from '@amzn/meridian/toaster';
import { Profile, TeamProfile, EmptyTeamProfile } from '../../Types';
import { FormattedMessage, useIntl } from 'react-intl';
import { ContactStatusOptions } from '@amzn/aet-contactrecommendation-kit';
import { ExceptionStatus } from '@amzn/aet-dynamo-kit';
import {
  transformOutliersToCategories,
  calculateOMMetrics,
  buildCSAToTMAliasMap,
  calculateExceptionMetrics,
  getPastWeek,
  buildCurrentCSAToTMMap,
} from './helpers';
import { OMOutliersTable } from './components/OutlierTable/OMOutliersTable';
import { OMExceptionsTable } from './components/OutlierTable/OMExceptionsTable';
import { SortDirection } from '../../Types/SortDirection';
import { OMPendingOutliersCard } from './components/OMPendingOutliersCard';
import { ExceptionOverviewCard } from './components/ExceptionOverviewCard';
import { TableSortDirection } from '@amzn/meridian/table/table';
import Naws from '../../api/Naws';
import DataSource from '../../api';
import DataState from '../../Types/DataState';
import { OutlierTimeOption } from '@amzn/aet-dynamo-kit';
import OMOutlierFilter from './components/OMOutlierFilter';
import { useOutlierFilters } from './hooks/useOutlierFilters';
import {
  FilterValues,
  OMOutlierSummaryStats,
  OutlierException,
  OMExceptionMetrics,
} from './types/types';
import { getLocale } from '../../config/Config';
import {
  OUTLIER_TM_INAPP_NOTIF_MESSAGE,
  OUTLIER_TM_INAPP_NOTIF_TITLE,
  OUTLIER_TM_EMAIL_TEMPLATE,
  NotificationType,
} from './types/constants';
import { DEFAULT_LOCALE } from '../../Util/Locales';
import { generateAbsoluteInspireUrl, urls } from '../../Util';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import OutlierManagementTMHome from './OutlierManagementTMHome';
import { useToaster } from '../../Util/AppToaster';
import useInspireOptions from '../../hooks/useInspireOptions';
import useOutliersForManager from '../../api/Outlier/useOutliersForManager';

interface OutlierManagementOMHomeProps {
  profile: Profile;
  profileState: string;
}

const convertToTableSortDirection = (
  direction: SortDirection
): TableSortDirection => {
  return direction === SortDirection.ASCENDING ? 'ascending' : 'descending';
};

const convertFromTableSortDirection = (
  direction: TableSortDirection
): SortDirection => {
  return direction === 'ascending'
    ? SortDirection.ASCENDING
    : SortDirection.DESCENDING;
};

export const OutlierManagementOMHome: React.FC<
  OutlierManagementOMHomeProps
> = ({ profile, profileState }) => {
  const {
    requesterUserID,
    requesterUsername,
    isExclusivelyOm,
    cscEntityPrefix,
  } = useInspireOptions();
  const intl = useIntl();
  const { openToast } = useToaster();

  // Toasts for Approve/Reject exception results
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);

  const [tmTableSort, setTMTableSort] = useState({
    sortColumn: 'requiredRemaining',
    sortDirection: convertToTableSortDirection(SortDirection.DESCENDING),
  });

  const [exceptionsTableSort, setExceptionsTableSort] = useState({
    sortColumn: 'contactId',
    sortDirection: convertToTableSortDirection(SortDirection.ASCENDING),
  });
  const [outlierNotification, setOutlierNotification] = useState(
    DataState.INITIALIZED
  );

  const apiNaws: typeof Naws = DataSource.getDataFeed('NAWS');

  // TeamProfile state for getting current TM-CSA relationships
  const [teamProfile, setTeamProfile] = useState<TeamProfile>(EmptyTeamProfile);
  const [teamProfileState, setTeamProfileState] = useState(DataState.LOADING);

  const [updateOutlierExceptionData, setUpdateOutlierExceptionData] =
    useState();
  const [updateOutlierExceptionDataState, setUpdateOutlierExceptionDataState] =
    useState(DataState.INITIALIZED);

  const [exceptionProcessingInProgress, setExceptionProcessingInProgress] =
    useState(false);

  const [currentTab, setCurrentTab] = useState('tab-tm-view');

  // Fetch TeamProfile data to get current TM-CSA relationships
  useEffect(() => {
    if (profile.user.userID && cscEntityPrefix) {
      const getTeamProfileDataQuery = apiNaws.prepareTeamProfileDataQuery(
        profile.user.userID,
        cscEntityPrefix
      );
      apiNaws.getTeamProfileData(
        getTeamProfileDataQuery,
        setTeamProfile,
        setTeamProfileState,
        'cache-first'
      );
    }
  }, [profile.user.userID, cscEntityPrefix]);

  // Build current CSA to TM map from TeamProfile
  // This map represents the current org structure, not stale data from outliers
  const currentCSAToTMMap = useMemo(() => {
    if (
      teamProfileState === DataState.LOADED &&
      teamProfile !== EmptyTeamProfile
    ) {
      return buildCurrentCSAToTMMap(teamProfile);
    }
    return undefined;
  }, [teamProfile, teamProfileState]);

  console.log({ teamProfile, currentCSAToTMMap });

  useEffect(() => {
    if (
      updateOutlierExceptionDataState === DataState.LOADED ||
      updateOutlierExceptionDataState === DataState.ERROR
    ) {
      const isApproveRejectSuccessful =
        updateOutlierExceptionDataState === DataState.LOADED;

      if (isApproveRejectSuccessful) {
        setShowSuccessToast(true);
        refetchOutliers()
          .catch(error => {
            console.error('Failed to refetch outliers:', error);
          })
          .finally(() => {
            setExceptionProcessingInProgress(false);
            setUpdateOutlierExceptionDataState(DataState.INITIALIZED);
          });
      } else {
        setShowErrorToast(true);
        setExceptionProcessingInProgress(false);
        setUpdateOutlierExceptionDataState(DataState.INITIALIZED);
      }
    }
  }, [updateOutlierExceptionDataState]);

  // The latest outlier data belong to past week.
  const pastWeek = getPastWeek();

  const {
    filters,
    updateFilters,
    clearFilters,
    removeFilter,
    filterCategories: filterCategoriesByFilters,
    getOMOutlierFilterInfoStripText,
  } = useOutlierFilters({
    initialTimeOption: pastWeek,
    initialTimeOptions: [pastWeek],
    initiaViewDataBy: OutlierTimeOption.WEEKLY,
    categories: [],
    associates: [],
    tmList: [],
    initialStatus: ContactStatusOptions.UNDEFINED,
    isOMException: true,
  });

  const {
    loading: outliersLoading,
    error: outliersError,
    data: outliers,
    refetch: refetchOutliers,
  } = useOutliersForManager(
    profile.user.userName,
    true,
    filters.viewDataBy,
    filters.timeOptions
  );
  const csaToTMAliasMap = buildCSAToTMAliasMap(outliers);
  const omHasAssociates = !isExclusivelyOm;
  const outlierCategories = useMemo(
    () => transformOutliersToCategories(outliers, true, csaToTMAliasMap),
    [outliers]
  );
  const categories = useMemo(
    () => [...new Set(outlierCategories.map(cat => cat.title))],
    [outlierCategories]
  );
  const tmList = useMemo(() => {
    const tmStats = calculateOMMetrics(
      outlierCategories,
      csaToTMAliasMap,
      profile.directReports,
      currentCSAToTMMap
    );
    return tmStats.flatMap(tmOutlierInfo => tmOutlierInfo.tmAlias);
  }, [outlierCategories, currentCSAToTMMap]);

  const filteredCategories = filterCategoriesByFilters(outlierCategories);
  // TODO: Add stronger types here once the backend is working with the frontend
  const {
    outlierData,
    exceptionData,
    exceptionMetrics,
  }: {
    outlierData: OMOutlierSummaryStats[];
    exceptionData: OutlierException[];
    exceptionMetrics: OMExceptionMetrics;
  } = useMemo(() => {
    const tmStats = calculateOMMetrics(
      filteredCategories,
      csaToTMAliasMap,
      profile.directReports,
      currentCSAToTMMap
    );
    const { exceptions, metrics } = calculateExceptionMetrics(
      filteredCategories,
      csaToTMAliasMap,
      requesterUserID,
      cscEntityPrefix
    );

    return {
      outlierData: tmStats,
      exceptionData: exceptions,
      exceptionMetrics: metrics,
    };
  }, [filteredCategories, profile, currentCSAToTMMap]);

  const filteredExceptionData = useMemo((): OutlierException[] => {
    return exceptionData.filter(
      exception => exception.status === ExceptionStatus.PENDING
    );
  }, [exceptionData]);

  const sortedOutlierData = useMemo(() => {
    return [...outlierData].sort((a, b) => {
      switch (tmTableSort.sortColumn) {
        case 'tmAlias':
          return convertFromTableSortDirection(tmTableSort.sortDirection) ===
            SortDirection.ASCENDING
            ? a.tmAlias.localeCompare(b.tmAlias)
            : b.tmAlias.localeCompare(a.tmAlias);
        case 'totalOutliers':
          return convertFromTableSortDirection(tmTableSort.sortDirection) ===
            SortDirection.ASCENDING
            ? a.totalOutliers - b.totalOutliers
            : b.totalOutliers - a.totalOutliers;
        case 'pendingOutliers':
          return convertFromTableSortDirection(tmTableSort.sortDirection) ===
            SortDirection.ASCENDING
            ? a.pendingOutliers - b.pendingOutliers
            : b.pendingOutliers - a.pendingOutliers;
        case 'hasRepeatOutliers':
          return convertFromTableSortDirection(tmTableSort.sortDirection) ===
            SortDirection.ASCENDING
            ? Number(a.hasRepeatOutliers) - Number(b.hasRepeatOutliers)
            : Number(b.hasRepeatOutliers) - Number(a.hasRepeatOutliers);
        default:
          return 0;
      }
    });
  }, [outlierData, tmTableSort.sortColumn, tmTableSort.sortDirection]);

  const sortedExceptionData = useMemo(() => {
    return [...filteredExceptionData].sort((a, b) => {
      switch (exceptionsTableSort.sortColumn) {
        case 'contactId':
          return convertFromTableSortDirection(
            exceptionsTableSort.sortDirection
          ) === SortDirection.ASCENDING
            ? a.contactId.localeCompare(b.contactId)
            : b.contactId.localeCompare(a.contactId);
        case 'tmAlias':
          return convertFromTableSortDirection(
            exceptionsTableSort.sortDirection
          ) === SortDirection.ASCENDING
            ? a.tmAlias.localeCompare(b.tmAlias)
            : b.tmAlias.localeCompare(a.tmAlias);
        case 'csaAlias':
          return convertFromTableSortDirection(
            exceptionsTableSort.sortDirection
          ) === SortDirection.ASCENDING
            ? a.csaAlias.localeCompare(b.csaAlias)
            : b.csaAlias.localeCompare(a.csaAlias);
        case 'category':
          return convertFromTableSortDirection(
            exceptionsTableSort.sortDirection
          ) === SortDirection.ASCENDING
            ? a.category.localeCompare(b.category)
            : b.category.localeCompare(a.category);
        default:
          return 0;
      }
    });
  }, [
    filteredExceptionData,
    exceptionsTableSort.sortColumn,
    exceptionsTableSort.sortDirection,
  ]);

  const metrics = useMemo(
    () => ({
      totalOutliers: outlierData.reduce((sum, tm) => sum + tm.totalOutliers, 0),
      pendingOutliers: outlierData.reduce(
        (sum, tm) => sum + tm.pendingOutliers,
        0
      ),
      reviewedOutliers: outlierData.reduce(
        (sum, tm) => sum + (tm.totalOutliers - tm.pendingOutliers),
        0
      ),
      totalTMs: profile.directReports.length,
      tmsWithOutliers: outlierData.length,
      tmsWithRepeatOutliers: outlierData.filter(tm => tm.hasRepeatOutliers)
        .length,
      requiredRemaining: outlierData.reduce(
        (sum, tm) => sum + tm.requiredRemaining,
        0
      ),
      totalRequired: outlierData.reduce((sum, tm) => sum + tm.totalRequired, 0),
    }),
    [outlierData, profile.directReports.length]
  );

  const handleTMTableSort = ({
    sortColumn,
    sortDirection,
  }: {
    sortColumn: string;
    sortDirection: TableSortDirection;
  }) => {
    setTMTableSort({
      sortColumn,
      sortDirection,
    });
  };

  const handleExceptionsTableSort = ({
    sortColumn,
    sortDirection,
  }: {
    sortColumn: string;
    sortDirection: TableSortDirection;
  }) => {
    setExceptionsTableSort({
      sortColumn,
      sortDirection,
    });
  };

  const handleUpdateOutlierException = async (
    userId: string,
    deliveredToUsername: string,
    contactId: string,
    exceptionStatus: ExceptionStatus,
    outlierCalYear: string
  ) => {
    setExceptionProcessingInProgress(true);
    const queryParams = apiNaws.prepareQueryForUpdateOutlierException(
      userId,
      deliveredToUsername,
      contactId,
      exceptionStatus,
      outlierCalYear
    );

    await apiNaws.updateOutlierException(
      queryParams,
      setUpdateOutlierExceptionData,
      setUpdateOutlierExceptionDataState
    );
  };

  const sendOutlierReminder = async (
    tmIdWithCSCPrefix: string,
    omIdWithCSCPrefix: string,
    tmFullName: string,
    tmUsername: string,
    outlierCount: number
  ) => {
    try {
      // an OM is only able to send an email or notification to a TM that is a current direct report.
      if (tmIdWithCSCPrefix && tmFullName) {
        const emailQueryParams = apiNaws.prepareUpdateAppNotificationInput(
          omIdWithCSCPrefix,
          tmIdWithCSCPrefix,
          tmUsername,
          NotificationType.EMAIL,
          {
            TM_FULL_NAME: tmFullName,
            OUTLIER_COUNT: outlierCount,
            OUTLIER_URL: generateAbsoluteInspireUrl(urls.outlierManagement),
          },
          OUTLIER_TM_EMAIL_TEMPLATE,
          getLocale() ?? DEFAULT_LOCALE
        );
        await apiNaws.sendNotification(emailQueryParams);

        // inapp notif
        const inAppQueryParams = apiNaws.prepareUpdateAppNotificationInput(
          omIdWithCSCPrefix,
          tmIdWithCSCPrefix,
          tmUsername,
          NotificationType.INAPP,
          {
            count: outlierCount,
          },
          undefined,
          getLocale() ?? DEFAULT_LOCALE,
          OUTLIER_TM_INAPP_NOTIF_TITLE,
          OUTLIER_TM_INAPP_NOTIF_MESSAGE
        );
        await apiNaws.sendNotification(inAppQueryParams);

        openToast({
          children: (
            <FormattedMessage
              id='cs_perfportal_outlier_notification_sent'
              defaultMessage='A notification was successfully sent to {alias}'
              values={{
                alias: tmUsername,
              }}
            />
          ),
          alertType: 'success',
        });
        return true;
      }
    } catch (error) {
      openToast({
        children: (
          <FormattedMessage
            id='cs_perfportal_outlier_notification_send_error'
            defaultMessage='Something went wrong and the notification to {alias} failed to send. Please try again.'
            values={{
              alias: tmUsername,
            }}
          />
        ),
        alertType: 'error',
      });
    }
    return false;
  };

  const handleOnApproveException = async (
    userId: string,
    deliveredToUsername: string,
    contactId: string,
    outlierCalYear: string
  ) => {
    await handleUpdateOutlierException(
      userId,
      deliveredToUsername,
      contactId,
      ExceptionStatus.ACCEPTED,
      outlierCalYear
    );
  };

  const handleOnRejectException = async (
    userId: string,
    deliveredToUsername: string,
    contactId: string,
    outlierCalYear: string
  ) => {
    await handleUpdateOutlierException(
      userId,
      deliveredToUsername,
      contactId,
      ExceptionStatus.DENIED,
      outlierCalYear
    );
  };

  const handleFilterChange = (newFilters: Partial<FilterValues>) => {
    updateFilters(newFilters);
  };

  const filterInfoStripText = getOMOutlierFilterInfoStripText();

  const onCloseToast = useCallback(() => {
    setShowSuccessToast(false);
    setShowErrorToast(false);
    setExceptionProcessingInProgress(false);
  }, []);

  const createToastMessage = ({
    id,
    defaultMessage,
    success,
  }: {
    id: string;
    defaultMessage: string;
    success: boolean;
  }) => {
    return (
      <Toaster
        toasts={[
          {
            id,
            timeout: 30000,
          },
        ]}
        onCloseToast={onCloseToast}
      >
        {toast => (
          <Alert
            toast={true}
            onClose={toast.onClose}
            size='medium'
            type={success ? 'success' : 'error'}
          >
            <FormattedMessage id={id} defaultMessage={defaultMessage} />
          </Alert>
        )}
      </Toaster>
    );
  };

  const omViewBoxes = () => {
    return (
      <>
        <Box
          spacingInset={
            omHasAssociates ? `none 500 500 none` : `none 500 500 500`
          }
        >
          <Row
            spacing='large'
            alignmentVertical='top'
            widths={['grid-4', 'grid-8']}
          >
            <Column spacingInset='none 500 none none'>
              <Box>
                <OMPendingOutliersCard
                  metrics={metrics}
                  tmStats={outlierData}
                  viewDataBy={filters.viewDataBy}
                />
              </Box>
            </Column>

            <Column spacing='medium'>
              <Box width='100%'>
                <Row widths='fill' alignmentVertical='center'>
                  <Box type='fill' spacingInset='100 100 100 100'>
                    <OMOutlierFilter
                      title={filterInfoStripText}
                      filters={filters}
                      tmList={tmList}
                      categories={categories}
                      onFilterChange={handleFilterChange}
                      onClearFilters={clearFilters}
                    />
                  </Box>
                </Row>
              </Box>
              <OMOutliersTable
                data={sortedOutlierData}
                sortColumn={tmTableSort.sortColumn}
                sortDirection={tmTableSort.sortDirection}
                onSort={handleTMTableSort}
                onSendReminder={sendOutlierReminder}
              />
            </Column>
          </Row>
        </Box>

        <Box
          spacingInset={
            omHasAssociates ? `200 500 500 none` : `200 500 500 500`
          }
        >
          <Text type='h400'>
            <FormattedMessage
              id='cs_perfportal_contacts_flagged_invalid'
              defaultMessage='Contacts Flagged Invalid'
            />
          </Text>
        </Box>

        <Box
          spacingInset={
            omHasAssociates ? `200 500 none none` : `200 500 none 500`
          }
        >
          <Row
            spacing='large'
            alignmentVertical='top'
            widths={['grid-4', 'grid-8']}
          >
            <Column spacingInset='none 500 none none'>
              <Box>
                <ExceptionOverviewCard
                  metrics={exceptionMetrics}
                  tmStats={outlierData}
                />
              </Box>
            </Column>

            <Column>
              <Box width='100%'>
                <OMExceptionsTable
                  data={sortedExceptionData}
                  sortColumn={exceptionsTableSort.sortColumn}
                  sortDirection={exceptionsTableSort.sortDirection}
                  exceptionProcessingInProgress={exceptionProcessingInProgress}
                  onSort={handleExceptionsTableSort}
                  onApprove={handleOnApproveException}
                  onReject={handleOnRejectException}
                />
              </Box>
            </Column>
          </Row>
        </Box>
      </>
    );
  };

  const renderMainContent = () => {
    if (
      outliersLoading &&
      updateOutlierExceptionDataState !== DataState.LOADED // Do not reload the page after approve/reject button is completed.
    ) {
      return (
        <Row alignmentHorizontal='center'>
          <Loader type='circular' size='large' />
        </Row>
      );
    }

    if (outliersError) {
      return (
        <Alert type='error'>
          <FormattedMessage
            id='cs_perfportal_error_reason_1'
            defaultMessage='Your credentials may be incorrect or inadequate.'
          />
        </Alert>
      );
    }

    return (
      <Box>
        {showSuccessToast &&
          createToastMessage({
            // TODO: Refine Success/Error toast messages
            id: 'cs_perfportal_om_flagged_contact_handling_success',
            defaultMessage:
              'Thank you for your evaluation. Flagged contact is handled successfully.',
            success: true,
          })}

        {showErrorToast &&
          createToastMessage({
            id: 'cs_perfportal_om_flagged_contact_handling_error',
            defaultMessage:
              'There was a technical issue when handling your request. Please try again later.',
            success: false,
          })}
        <Box spacingInset='400 500 500 500'>
          <Text type='h400'>
            <FormattedMessage
              id='cs_perfportal_om_outlier_metrics'
              defaultMessage='Outlier Metrics'
            />
          </Text>
        </Box>
        {omHasAssociates ? (
          <Box spacingInset='400 700 500 500'>
            <Column>
              <TabGroup
                value={currentTab}
                onChange={setCurrentTab}
                aria-labelledby='tablist-heading'
                type='default'
              >
                <Tab
                  value='tab-tm-view'
                  aria-controls='tabpanel-tm-view'
                  id='tab-tm-view'
                >
                  <FormattedMessage
                    id='cs_perfportal_om_outlier_metrics_tm_view'
                    defaultMessage='TM View'
                  />
                </Tab>
                <Tab
                  value='tab-csa-view'
                  aria-controls='tabpanel-csa-view'
                  id='tab-csa-view'
                >
                  <FormattedMessage
                    id='cs_perfportal_om_outlier_metrics_csa_view'
                    defaultMessage='Associate View'
                  />
                </Tab>
              </TabGroup>
              {currentTab === 'tab-tm-view' && (
                <div
                  role='tabpanel'
                  id='tabpanel-tm-view'
                  aria-labelledby='tab-tm-view'
                >
                  {omViewBoxes()}
                </div>
              )}
              {currentTab === 'tab-csa-view' && (
                <div
                  role='tabpanel'
                  id='tabpanel-csa-view'
                  aria-labelledby='tab-csa-view'
                >
                  <OutlierManagementTMHome
                    profile={profile}
                    profileState={profileState}
                  ></OutlierManagementTMHome>
                </div>
              )}
            </Column>
          </Box>
        ) : (
          omViewBoxes()
        )}
      </Box>
    );
  };

  return <Box>{renderMainContent()}</Box>;
};

export default OutlierManagementOMHome;
